import { DownOutlined } from '@ant-design/icons';
import { cloneElement, type CSSProperties, useMemo, useRef } from 'react';

import SelectPortal from '@/components/Select/SelectPortal';
import useFixed from '@/hooks/pure/useFixed';
import useStates from '@/hooks/pure/useStates';
import { cn } from '@/lib/cn';

import SorterDropdown from './SorterDropdown';
import type { SortOptionT } from './types';

type Props = {
  options: SortOptionT[];
  currSortStatus: NotNullableSortStatusT;
  onSortChange?: (sortStatus: NotNullableSortStatusT) => void;
  style?: CSSProperties;
  useOrderBy?: boolean;
  className?: string;
  triggerNode?: React.ReactElement;
};

type State = {
  sortDropdownOpen: boolean;
  groupbyDropdownOpen: boolean;
};

const Sorter = (props: Props) => {
  const { state, setValue } = useStates<State>({
    sortDropdownOpen: false,
    groupbyDropdownOpen: false,
  });
  const sorterRef = useRef<HTMLDivElement>(null);

  const sorterPos = useFixed(sorterRef, {
    offset: {
      y: -4,
    },
    reflect: {
      x: true,
      y: true,
    },
  });

  const useOrderBy =
    typeof props.useOrderBy === 'undefined' ? true : props.useOrderBy;

  const trigger = useMemo(() => {
    if (props.triggerNode) {
      return cloneElement(props.triggerNode, {
        ref: sorterRef,
        onClick: () => setValue('sortDropdownOpen', !state.sortDropdownOpen),
      });
    }
    return (
      <div
        className={cn(
          'flex items-center h-10 px-2 cursor-pointer gap-1',
          props.className,
        )}
        style={props.style}
        ref={sorterRef}
        onClick={() => setValue('sortDropdownOpen', !state.sortDropdownOpen)}
      >
        <span className="text-xs text-gray-700">Sort by</span>
        {props.currSortStatus && (
          <span className="text-xs text-gray-800">
            {
              props.options.find(
                (col) => col.sortKey === props.currSortStatus?.sortBy,
              )?.sortName
            }
          </span>
        )}
        <DownOutlined className="text-xs text-gray-700 cursor-pointer" />
      </div>
    );
  }, [
    props.className,
    props.currSortStatus,
    props.options,
    props.style,
    props.triggerNode,
    setValue,
    state.sortDropdownOpen,
  ]);

  return (
    <>
      {trigger}
      <SelectPortal
        status={!!state.sortDropdownOpen}
        close={() => setValue('sortDropdownOpen', false)}
        style={sorterPos}
      >
        <SorterDropdown
          options={props.options}
          currSortStatus={props.currSortStatus}
          onChange={props.onSortChange}
          useOrderBy={useOrderBy}
        />
      </SelectPortal>
    </>
  );
};

export default Sorter;
